import React from 'react';
import { connect } from 'react-redux';
import classnames from 'classnames';
import { withRouter } from 'next/router';
import PropTypes from 'prop-types';

import HeaderSearchBar from 'components/headerSearchBar';
import rbNavClasses from 'components/resumeBuilder/nav/index.module.css';
import { withTracking } from 'hooks';
import {
  AuthMenu,
  Avatar,
  ExploreDropDown,
  ExploreLink,
  Hamburger,
  Link,
  LogOutButton,
} from 'layouts/header';
import Logo from 'layouts/header/Logo';
import {
  getSavedSubscription,
  pushSubscriptionNotification,
} from 'services/pushNotificationsService';
import { openLoginModal } from 'store/modals/actions';
import {
  buildCityPageUrl,
  buildCmpSubpagesUrls,
  buildComboCmpUrl,
  buildCompanyJobsCityUrl,
  freezeBodyScroll,
  getStateName,
  openInCurrentTab,
  resizeViewHeight,
  setCookie,
  unFreezeBodyScroll,
} from 'utils';
import { CMP_SUBPAGES, TRACKING } from 'utils/constant';
import { AUTOSUGGEST_CATEGORIES, ENTITY_TYPE } from 'utils/constants-v2';

import { COMMON_LINKS } from './navigation/data';
import { MobileNavigation } from './navigation/mobile';
import { Navigation, SEONavigation } from './navigation';

import subheaderStyles from '../../css/subHeader.module.css';
import classes from './index.module.css';

const EMPLOYER_LINK_TRACKING = '#source=post_job_header_button';

/**
 * COMPONENT_CACHE_KEY is for storing user's entered data from store to localstorage
 */
const COMPONENT_CACHE_KEY = 'siteMapSearch';

class Header extends React.Component {
  static propTypes = {
    user: PropTypes.any,
    showSubHeader: PropTypes.bool,
    notificationDot: PropTypes.bool,
    router: PropTypes.any.isRequired,
    openLoginModal: PropTypes.func.isRequired,
    openSignUpModal: PropTypes.func.isRequired,
    desktopDevice: PropTypes.bool,
  };

  static defaultProps = {
    user: null,
    showSubHeader: false,
    showClusterSubheader: false,
    notificationDot: false,
    desktopDevice: undefined,
  };

  state = {
    headerMenuExpanded: false,
    headerMenuSearchExpanded: false,
    mobileMenuOpenedBefore: false,
    mobileSearchMenuOpenedBefore: false,
    profileMenu: false,
  };

  firstSearchBarCategory = AUTOSUGGEST_CATEGORIES.SUGGESTIONS;

  secondSearchBarCategory = AUTOSUGGEST_CATEGORIES.LOCATION;

  componentDidMount() {
    resizeViewHeight();
    window.addEventListener('resize', resizeViewHeight);
    document.addEventListener('mousedown', this.handleClickOutside);
  }

  setWrapperRef = (node) => {
    this.wrapperRef = node;
  };

  handleClickOutside = (event) => {
    if (this.wrapperRef && !this.wrapperRef.contains(event.target)) {
      this.setState({ profileMenu: false });
    }
  };

  toggleMobileMenu = (event) => {
    if (!event) return;

    const { headerMenuExpanded, mobileMenuOpenedBefore } = this.state;

    if (!headerMenuExpanded && !mobileMenuOpenedBefore) {
      this.setState({ mobileMenuOpenedBefore: true });
    }
    this.setState({ headerMenuSearchExpanded: false });

    if (headerMenuExpanded) {
      unFreezeBodyScroll();
    } else {
      freezeBodyScroll();
    }

    this.setState({ headerMenuExpanded: !headerMenuExpanded });
  };

  toggleMobileSearchMenu = () => {
    const { headerMenuSearchExpanded, mobileSearchMenuOpenedBefore } = this.state;

    if (!headerMenuSearchExpanded && !mobileSearchMenuOpenedBefore) {
      this.setState({ mobileSearchMenuOpenedBefore: true });
    }
    this.setState({ headerMenuExpanded: false });
    this.setState({ headerMenuSearchExpanded: !headerMenuSearchExpanded });
  };

  toggleProfileMenu = () => {
    const { profileMenu } = this.state;
    this.setState({ profileMenu: !profileMenu });
  };

  hideProfileMenu = () => {
    const { profileMenu } = this.state;
    if (profileMenu) {
      this.setState({ profileMenu: false });
    }
  };

  goToMatches = () => {
    window.location.href = '/matches/';
  };

  renderExploreLink = (className = '', logged = false) => {
    const { router } = this.props;
    const { profileMenu } = this.state;

    return (
      <div
        className={classnames('position-relative', classes.discoverMenu, {
          'd-none d-lg-block': !logged,
        })}
      >
        <ExploreLink isActive={router.asPath.includes('/explore')} className={className} />
        <ExploreDropDown exploreDropDownVisible={!profileMenu} />
      </div>
    );
  };

  renderMobileExploreList = () => {
    const list = [
      { href: '/careers/', title: 'By Job Titles' },
      { href: '/colleges-and-majors/', title: 'By College Majors' },
      { href: '/company/', title: 'By Companies' },
      { href: '/jobs/', title: 'Jobs Near Me' },
      { href: '/advice/', title: 'Job Search Advice' },
      { href: '/salaries/', title: 'Salaries' },
    ];
    return <ul>{list.map((link) => this.renderLinkWithLi(link, true))}</ul>;
  };

  /**
   * @param {object} link link options
   * @param {string} link.title title of the link
   * @param {string} link.href url
   */
  renderLinkWithLi = (link) => (
    <li key={link.title}>
      <a href={link.href} className="d-flex align-items-center justify-content-between">
        {link.title}
        <i
          className={classnames('fa', classes.faChevronRight, classes.subFaChevronRight)}
          aria-hidden="true"
        />
      </a>
    </li>
  );

  onSignIn = () => {
    const { openLoginModal } = this.props;
    const width = window?.innerWidth;

    if (width < 992) {
      this.toggleMobileMenu();
    }

    openLoginModal();
  };

  handleLogout = () => {
    // Unsubscribe to the Push notification
    const { user } = this.props;
    // Delete the cookie
    setCookie({
      item: '_zippia-push_sub',
      value: Date.now(),
      expiredays: 0,
      path: '/',
    });
    const subscription = getSavedSubscription();

    if (subscription) {
      // Make unsubscribe call for push signed in user
      const userId = subscription?.userId ?? user?.userId;
      if (userId) pushSubscriptionNotification(userId, 'unsubscribe', subscription, false);
    }
    window.location.href = '/auth/logout';
  };

  renderNavigation = (userLoggedIn, isCompanyUser) => {
    const { user, router, desktopDevice } = this.props;

    const { headerMenuExpanded, profileMenu } = this.state;

    return (
      <div className="d-flex align-items-center">
        <div className={classes.options}>
          <ul
            className={classnames(
              'nav navbar-nav header links flex-row mt-0 flex-nowrap',
              classes.nav
            )}
          >
            {isCompanyUser && (
              <Link
                title="Company Profile"
                href={`/employer/profile/${user?.employerData?.companyID}/`}
                isActive={router.asPath?.includes('/employer/profile/')}
              />
            )}
            <Link
              title="Post Job"
              href={`/employer/${EMPLOYER_LINK_TRACKING}`}
              isActive={router.asPath?.includes('/employer/')}
            />
          </ul>
        </div>
        {userLoggedIn ? (
          <>
            {!this.hideSearchBar() && (
              <i
                className="fa fa-search white-text mr-24 d-block d-lg-none"
                onClick={this.toggleMobileSearchMenu}
              ></i>
            )}

            <Avatar
              avatar={user?.photo}
              desktopDevice={desktopDevice}
              toggleProfileMenu={this.toggleProfileMenu}
              toggleMobileMenu={this.toggleMobileMenu}
              isCompanyUser={isCompanyUser}
              goToMatches={this.goToMatches}
              handleLogout={this.handleLogout}
              profileMenu={profileMenu}
              setWrapperRef={this.setWrapperRef}
            />
          </>
        ) : (
          <>
            <div className="ml-0">
              <div
                onClick={this.onSignIn}
                className={classnames(
                  'd-block d-lg-none white-text z-mr-20 font-14 text-uppercase cursor-pointer',
                  classes.mobileSignInBtn
                )}
              >
                Sign In
              </div>
              <AuthMenu onSignIn={this.onSignIn} />
            </div>
            {!this.hideSearchBar() && (
              <i
                className="fa fa-search white-text mr-24 d-block d-lg-none"
                onClick={this.toggleMobileSearchMenu}
              ></i>
            )}
            <Hamburger menuExpanded={headerMenuExpanded} onClick={this.toggleMobileMenu} />
          </>
        )}
      </div>
    );
  };

  renderMobileNav = (userLoggedIn, isCompanyUser, companyID) => {
    const getLinks = (options = {}) => {
      const { isCompleted } = options;

      const links = [];

      if (userLoggedIn) {
        if (isCompanyUser) {
          links.push({ text: 'Company Profile', href: `/employer/profile/${companyID}/` });
        }
        links.push(...COMMON_LINKS({ isLoggedIn: userLoggedIn && isCompleted }));
        links.push({ text: 'Salary Report', href: '/matches/salary/' });
        links.push({ text: 'Career Insights', href: '/dashboard/' });

        links.push({ text: 'Preferences', href: '/profile/jobs/' });
      }

      return links;
    };

    return <MobileNavigation getCommonLinks={getLinks} onLinkClick={this.toggleMobileMenu} />;
  };

  hideSearchBar = () => {
    const searchBarExcludedURLList = ['/matches/', '/resume-builder'];

    return searchBarExcludedURLList.find((x) => {
      return this.props?.router?.asPath.includes(x);
    });
  };

  getShowSponsoredJobPopout = () => {
    const { showSponsoredJobs, showEmployerPopout, criteria } = this.props;
    if (typeof showSponsoredJobs !== 'boolean' && typeof showEmployerPopout !== 'boolean') {
      return !!criteria?.title;
    }
    return !!showSponsoredJobs || !!showEmployerPopout;
  };

  handleSearchBarSubmit = (value) => {
    const { tracking, user } = this.props;
    // first search bar category might be "jobTitle" or "suggestions"
    // depending upon the value of A/B test userGroup "AutoSuggestTest"
    // second search bar category will always be "location"
    let url = '';
    const logPayload = {
      userId: user.userId,
      pageCategory: window.pageCategory,
    };
    const firstSearchBarValue = value[this.firstSearchBarCategory];
    const secondSearchBarValue = value[this.secondSearchBarCategory];
    const location = secondSearchBarValue?.displayValue;
    let { city = '', state } = secondSearchBarValue?.valueObj || {};
    if (!city && state) {
      state = getStateName(state);
    }
    const displayValue = firstSearchBarValue?.displayValue;
    const entityType = firstSearchBarValue?.valueObj?.entityType;
    const entityValue =
      firstSearchBarValue?.valueObj?.original_name || firstSearchBarValue?.valueObj?.companyName;

    // if there is any url of sub pages
    if (firstSearchBarValue?.valueObj?.url) {
      // if location is pre-selected
      if (location && state) {
        if (entityType === ENTITY_TYPE.COMPANY && displayValue?.includes('Jobs')) {
          // Company + Location Jobs Page
          url = buildCompanyJobsCityUrl(firstSearchBarValue?.valueObj?.zippiaCppUrl, city, state);
        } else if (entityType === ENTITY_TYPE.TITLE && displayValue?.includes('Jobs')) {
          // Job Title + Location Jobs Page (Combo CMP)
          url = buildComboCmpUrl(entityValue, location);
        } else {
          // ignore the location
          url = firstSearchBarValue?.valueObj?.url;
        }
      }
      // else use the subpage url
      else {
        url = firstSearchBarValue?.valueObj?.url;
      }
    } else {
      // if location is pre-selected
      if (location && state) {
        if (entityType === ENTITY_TYPE.TITLE) {
          // Job Title + Location Jobs Page (Combo CMP)
          url = buildComboCmpUrl(entityValue, location);
        } else if (entityType === ENTITY_TYPE.COMPANY) {
          // Company + Location Jobs Page
          url = buildCompanyJobsCityUrl(firstSearchBarValue?.valueObj?.zippiaCppUrl, city, state);
        } else {
          url = buildCityPageUrl(city, getStateName(state));
        }
      }
      // else
      else {
        if (entityType === ENTITY_TYPE.TITLE) {
          // CMP
          url = buildCmpSubpagesUrls(entityValue, '', CMP_SUBPAGES.OVERVIEW);
        } else if (entityType === ENTITY_TYPE.COMPANY) {
          // CPP
          url = firstSearchBarValue?.valueObj?.zippiaCppUrl;
        }
      }
    }
    
    const title = firstSearchBarValue?.valueObj?.name;
    Object.assign(logPayload, {
      location,
      title: title ? { originalSearch: title, normalizedTitle: title } : undefined,
      titleResultFound: !!title,
      locationResultFound: !!location,
      company: firstSearchBarValue?.valueObj?.companyName || '',
      companyResultFound: !!firstSearchBarValue?.valueObj?.companyName,
    });

    const cachedData = JSON.parse(localStorage.getItem(COMPONENT_CACHE_KEY)) || {};
    localStorage.setItem(
      COMPONENT_CACHE_KEY,
      JSON.stringify({
        ...cachedData,
        COMPONENT_CACHE_KEY,
        input: {
          ...(cachedData.input || {}),
          [this.firstSearchBarCategory]: firstSearchBarValue,
          [this.secondSearchBarCategory]: secondSearchBarValue,
        },
      })
    );

    Object.assign(logPayload, {
      destiny: window.location.origin + url,
    });

    tracking.logActivity(logPayload, TRACKING.LOG_ACTIVITY.HEADER_SEARCH);
    if (url) openInCurrentTab(url);
  };

  retrieveSearchBarValueFromCache = (cacheKey) => {
    if (window) {
      const cachedData = JSON.parse(window.localStorage.getItem(cacheKey)) || {};
      return {
        [this.firstSearchBarCategory]: cachedData?.input?.[this.firstSearchBarCategory] || {},
        [this.secondSearchBarCategory]: cachedData?.input?.[this.secondSearchBarCategory] || {},
      };
    } else {
      return {};
    }
  };

  renderHeaderSearchBar = (className, isMobile = false) => {
    const searchBarValueFromCache = this.retrieveSearchBarValueFromCache(COMPONENT_CACHE_KEY);
    const placeholder =
      this.firstSearchBarCategory === AUTOSUGGEST_CATEGORIES.SUGGESTIONS
        ? 'Job Title or Company'
        : 'Search for Job Titles';

    return (
      <HeaderSearchBar
        searchBars={[
          {
            category: this.firstSearchBarCategory,
            inputProps: {
              className,
              placeholder,
            },
            prefillValue: searchBarValueFromCache?.[this.firstSearchBarCategory] || '',
            otherAutosuggestProps: {
              indexableOnly: true,
            },
            submitOnSelect: true,
          },
          {
            category: this.secondSearchBarCategory,
            inputProps: {
              className,
              placeholder: 'Location',
            },
            prefillValue: searchBarValueFromCache?.[this.secondSearchBarCategory] || '',
            otherAutosuggestProps: {
              extended: true,
            },
          },
        ]}
        isMobile={isMobile}
        onSubmit={this.handleSearchBarSubmit}
      />
    );
  };

  render() {
    const { user, showSubHeader, showClusterSubheader, resumeBuilder } = this.props;

    const {
      headerMenuExpanded,
      headerMenuSearchExpanded,
      mobileMenuOpenedBefore,
      mobileSearchMenuOpenedBefore,
    } = this.state;

    const userLoggedIn = user?.isLoggedIn;
    const isCompanyUser = userLoggedIn && !!user?.employerData?.companyID;

    const {
      flow: { currentStepIndex },
      resumeBuilderState,
    } = resumeBuilder;

    const showHeader = !!(
      currentStepIndex > 2 &&
      currentStepIndex !== 10 &&
      resumeBuilderState[currentStepIndex].navLabel
    );

    if (showHeader) {
      return null;
    }

    return (
      <header
        className={classnames('header header-new w-100', classes.header, {
          [classes.withClusterHeader]: showClusterSubheader,
          [subheaderStyles['with-subheader']]: showSubHeader,
          'without-subheader': !showSubHeader,
        })}
      >
        <nav className={classnames('navbar p-0', classes.navbar)}>
          <div
            className={classnames(
              'container d-flex justify-content-between flex-nowrap',
              classes.container,
              {
                'align-items-center': showHeader,
                [rbNavClasses['rb-header']]: showHeader,
              }
            )}
          >
            <div className="d-flex align-items-center">
              <Logo userLoggedIn={userLoggedIn} showSmallLogo={userLoggedIn} />
              {!showHeader && !isCompanyUser && !this.hideSearchBar() && (
                <div className="d-none d-lg-block z-ml-20">
                  {this.renderHeaderSearchBar('z-px-16 z-font-15 z-text-base')}
                </div>
              )}
              <Navigation />
            </div>
            {!showHeader && this.renderNavigation(userLoggedIn, isCompanyUser)}
          </div>
        </nav>

        {!showHeader && (
          <>
            {/* Mobile Drop Down menu */}
            <div
              className={classnames(classes.mobileMenu, {
                [classes.showMobileMenu]: headerMenuExpanded,
                [classes.hideMobileMenu]: !headerMenuExpanded && mobileMenuOpenedBefore,
              })}
            >
              <div className={classes.mobileNavigation}>
                {this.renderMobileNav(userLoggedIn, isCompanyUser, user?.employerData?.companyID)}
                <div className={classes.mobileSignBtns}>
                  <a
                    className={classnames(
                      'd-block z-br-4 z-py-8 text-center w-100 text-uppercase z-font-base z-leading-20px',
                      classes.postJobMobile
                    )}
                    href={`/employer/${EMPLOYER_LINK_TRACKING}`}
                  >
                    Post Job
                  </a>
                  {userLoggedIn ? (
                    <LogOutButton onClick={this.handleLogout} className="w-100" />
                  ) : (
                    <AuthMenu onSignIn={this.onSignIn} showMobile />
                  )}
                </div>
              </div>
            </div>

            {/* disable chrome autoprefill */}
            <form>
              <input
                type="text"
                id="username"
                autoComplete="off"
                style={{
                  width: 0,
                  height: 0,
                  visibility: 'hidden',
                  position: 'absolute',
                  left: 0,
                  top: 0,
                }}
              />
              <input
                type="password"
                autoComplete="off"
                style={{
                  width: 0,
                  height: 0,
                  visibility: 'hidden',
                  position: 'absolute',
                  left: 0,
                  top: 0,
                }}
              />
            </form>
            <div
              className={classnames(classes.mobileMenu, {
                [classes.showMobileMenu]: headerMenuSearchExpanded,
                [classes.hideMobileMenu]: !headerMenuSearchExpanded && mobileSearchMenuOpenedBefore,
              })}
            >
              {this.renderHeaderSearchBar('', true)}
            </div>
          </>
        )}
        <SEONavigation />
      </header>
    );
  }
}
const mapDispatchToProps = {
  openLoginModal,
};

export default withTracking(
  withRouter(
    connect(
      ({ user, resumeBuilder }) => ({
        user,
        resumeBuilder,
      }),
      mapDispatchToProps
    )(Header)
  )
);
